import type { DynamicsChartId } from './dynamicsPrefs';

/** Short modal copy for the insight bulb on each Dynamics card. */
export const DYNAMICS_CARD_INSIGHTS: Record<
  DynamicsChartId,
  { title: string; bullets: readonly string[] }
> = {
  A: {
    title: 'What Plan vs Done shows',
    bullets: [
      'Compare Plan to Done each bucket — a steady gap means commitments outpace delivery.',
      'Rising Dev or QA debt while Done is flat often signals work piling up before deadlines.',
      'Spikes in verification failed point to quality gates worth a closer look (tests, merges).',
      'Repeated gaps between Plan and Done matter more than one weak sprint.',
    ],
  },
  B: {
    title: 'What Bug flow shows',
    bullets: [
      'The stacked area’s height is total open bugs — growing means the backlog is swelling.',
      'Watch which coloured band grows: that status is where work is stacking.',
      'A shrinking stack usually means closure is outpacing new bugs.',
    ],
  },
  C: {
    title: 'What Arrival vs Closure shows',
    bullets: [
      'Created vs resolved per period — are you draining the queue or feeding it?',
      'The shaded net line: above zero means backlog grew that period.',
      'Several periods with created much higher than resolved is a capacity or triage alert.',
    ],
  },
  D: {
    title: 'What MTTR dynamics shows',
    bullets: [
      'Line climbing over time? Bugs may sit open longer — worth checking load versus difficulty.',
      'Compare Highest through Low curves: uneven gaps hint where SLA-style priority slips.',
      'MTTR up while arrivals stay flat usually means throughput is trailing demand.',
      'A missing tier in a slice usually means no bugs at that priority were resolved there.',
    ],
  },
  E: {
    title: 'What Escaped defects shows',
    bullets: [
      'Bugs logged after release but tied to that version signal what slipped to production.',
      'Higher counts after a release warrant a retro or sharper release criteria.',
      'Trending down usually means escapes are better caught before ship — or taxonomy is tighter.',
    ],
  },
  G: {
    title: 'Simple takeaways · QA throughput',
    bullets: [
      'Each interval shows how often work moved into your done statuses (configured under Quality → Terminal).',
      'Coloured lines = who Jira logged on the transition; Unknown = author not recorded.',
      'Use for balance: one line far above the rest suggests uneven workload, not a scoreboard.',
      'Fewer transitions can mean less delivery upstream, not slower QA.',
    ],
  },
  H: {
    title: 'What Reopen dynamics shows',
    bullets: [
      'Counts transitions back from Done/Verified to In Progress — “we thought it was fixed”.',
      'Rising reopens suggest unstable fixes, unclear acceptance, or environment issues.',
      'Slice by period to correlate with releases or organisational change.',
    ],
  },
  I: {
    title: 'What Sprint predictability shows',
    bullets: [
      'Done ÷ Plan for each sprint — how much of what you planned actually shipped.',
      'Consistently under the reference line suggests planning optimism or interruptions.',
      'Stable runs above the line mean commitments you can negotiate with stakeholders.',
    ],
  },
  J: {
    title: 'What Bug origin shows',
    bullets: [
      'Stacks bugs by the reporter’s Team roster role (Profile → Team roster: name / Jira id).',
      'When no roster exists for the product, the chart falls back to simple name patterns (QA / Dev / Support).',
      'Reporters not on the roster are grouped using the same fallback rules as the Dev segment in that mode.',
    ],
  },
  K: {
    title: 'What Escape taxonomy shows',
    bullets: [
      'Share of escapes by root-cause bucket over time — regression, env, data, missed req, etc.',
      'Dominant stripes show where process or tests need investment.',
      'Needs the escape cause field wired in Quality settings plus consistent usage in Jira.',
    ],
  },
};
